import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// third party
import { useSnackbar } from 'notistack';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import useAuth from '../../../hooks/useAuth';
// utils
import cssStyles from '../../../utils/cssStyles';
import { userRedirectService, getDefaultRouteFromUserType } from '../../../utils/userRedirectService';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
//
import AccountPopover from './AccountPopover';
// import LanguagePopover from './LanguagePopover';
// import NotificationsPopover from './NotificationsPopover';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  backgroundColor: theme.palette.background.newneutral,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    // height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const isDesktop = useResponsive('up', 'lg');
  const { enqueueSnackbar } = useSnackbar();
  const { refreshAuth } = useAuth();

  // Check if we're in redirect mode
  const isRedirectMode = userRedirectService.isInRedirectMode();
  const redirectedUserInfo = userRedirectService.getRedirectedUserInfo();

  const handleGoBackToParent = async () => {
    try {
      // Show loading state
      enqueueSnackbar('Switching back to parent user...', { variant: 'info' });
      
      // Switch back to parent (updates localStorage)
      userRedirectService.switchBackToParent();

      // Redirect to the restored parent user's default route
      const parentUserType = localStorage.getItem('userType');
      const redirectPath = getDefaultRouteFromUserType(parentUserType);

      setTimeout(() => {
        window.location.href = redirectPath;
      }, 100);
    } catch (error) {
      console.error('Switch back failed');
      enqueueSnackbar('Failed to switch back to parent user', { variant: 'error' });
    }
  };
  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}

        {/* <Searchbar /> */}
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {/* Back to Parent Button - Only show when in redirect mode */}
          {isRedirectMode && (
            <Tooltip title={`Go back to parent user`}>
              <IconButton
                onClick={handleGoBackToParent}
                sx={{
                  bgcolor: (theme) => theme.palette.warning.lighter,
                  color: (theme) => theme.palette.warning.darker,
                  border: (theme) => `1px solid ${theme.palette.warning.main}`,
                  '&:hover': {
                    bgcolor: (theme) => theme.palette.warning.light,
                  },
                  mr: 1,
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {/* Show redirect indicator text */}
          {isRedirectMode && redirectedUserInfo && (
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                bgcolor: (theme) => theme.palette.warning.lighter,
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.warning.main}`,
                mr: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: (theme) => theme.palette.warning.darker,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                🔄 {redirectedUserInfo.username?.toUpperCase() || 'REDIRECTED USER'}
              </Typography>
            </Box>
          )}

          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
